import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, Calendar, BookOpen, Skull, Eye, FileText, Layers, Clock, Swords, Trophy, Newspaper, Compass, Star, ArrowRight, Check } from 'lucide-react';
import axios from 'axios';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/kmnn6rz3_proyectoresident.png";
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const heroVideos = [
  { title: 'Cuestionarios', url: '/videos/cuestionarios.mp4' },
  { title: 'Escape Room', url: '/videos/escape_room.mp4' },
  { title: 'Imagen DX', url: '/videos/imagen_dx.mp4' },
  { title: 'Flash Cards', url: '/videos/flash_cards.mp4' },
  { title: 'Simulacros', url: '/videos/simulacros.mp4' },
  { title: 'Duelos', url: '/videos/duelos.mp4' },
  { title: 'Presentaciones de Repaso', url: '/videos/presentaciones.mp4' },
];

const features = [
  {
    id: 'planificador',
    title: 'Planificador Inteligente de Estudio',
    description: 'Se adapta a tu tiempo y días disponibles. Organiza tu estudio con presentaciones, cuestionarios, escape rooms y simulacros de forma balanceada por área.',
    screenshot: '/screenshots/planificador.png',
    icon: Calendar,
    color: '#2563EB',
  },
  {
    id: 'cuestionarios',
    title: 'Cuestionarios de las GPCs',
    description: 'Basados en las Guías de Práctica Clínica oficiales. Cada pregunta incluye retroalimentación inmediata con la explicación de la respuesta correcta.',
    screenshot: '/screenshots/cuestionarios.png',
    icon: BookOpen,
    color: '#059669',
  },
  {
    id: 'escaperoom',
    title: 'Casos Clínicos Escape Room',
    description: 'Algoritmos diagnósticos interactivos donde cada decisión cuenta. Dos errores y no pasas el caso. Aprende medicina bajo presión real.',
    screenshot: '/screenshots/escaperoom.png',
    icon: Skull,
    color: '#DC2626',
  },
  {
    id: 'imagendx',
    title: 'Imágenes Diagnósticas',
    description: 'Colección completa de imágenes de todas las áreas médicas. Entrena tu ojo clínico con radiografías, tomografías, ECGs y más.',
    screenshot: '/screenshots/imagendx.png',
    icon: Eye,
    color: '#7C3AED',
  },
  {
    id: 'presentaciones',
    title: 'Presentaciones de Repaso',
    description: 'Los temas más importantes del examen de residencia, resumidos en presentaciones claras y directas para repasar lo esencial.',
    screenshot: '/screenshots/presentaciones.png',
    icon: FileText,
    color: '#EA580C',
    customRender: 'presentaciones',
  },
  {
    id: 'flashcards',
    title: 'Flash Cards Personales',
    description: 'Crea tus propias tarjetas de estudio o genera flash cards automáticas desde los cuestionarios. Guarda lo que necesitas recordar.',
    screenshot: '/screenshots/flashcards.png',
    icon: Layers,
    color: '#0891B2',
    customRender: 'flashcards',
  },
  {
    id: 'simulacros',
    title: 'Simulacros tipo ENARM',
    description: '280 preguntas en 5 horas con reloj cronómetro. Simula la experiencia real del examen de residencia para que llegues preparado.',
    screenshot: '/screenshots/simulacros.png',
    icon: Clock,
    color: '#BE123C',
    customRender: 'simulacros',
  },
  {
    id: 'duelos',
    title: 'Duelos entre Usuarios',
    description: 'Reta a tus amigos y compañeros a duelos de conocimiento. Un sistema tipo "Preguntados" con las perlas del ENARM para repasar compitiendo.',
    screenshot: '/screenshots/duelos.png',
    icon: Swords,
    color: '#CA8A04',
    customRender: 'duelos',
  },
  {
    id: 'ranking',
    title: 'Ranking del Día + Journal',
    description: 'Compite cada día por el Top 10. Los mejores se convierten en coautores de un "Journal del Día": un artículo médico inventado y gracioso que se publica como premio.',
    screenshot: '/screenshots/ranking.png',
    journalScreenshot: '/screenshots/journal.png',
    icon: Trophy,
    color: '#B45309',
    customRender: 'ranking',
  },
  {
    id: 'enarm_match',
    title: 'Selector de Especialidad',
    description: 'Un cuestionario que analiza tus gustos, preferencias y personalidad para ayudarte a descubrir qué especialidad médica va mejor contigo.',
    screenshot: '/screenshots/enarm_match.png',
    icon: Compass,
    color: '#4F46E5',
  },
];

const ranks = [
  { name: 'Estudiante de Medicina', key: 'estudiante_de_medicina' },
  { name: 'Interno de Pregrado', key: 'interno_de_pregrado' },
  { name: 'Médico Pasante', key: 'medico_pasante' },
  { name: 'Médico General', key: 'medico_general' },
  { name: 'Residente 1er Año', key: 'residente_primer_ano' },
  { name: 'Residente Último Año', key: 'residente_ultimo_ano' },
  { name: 'Jefe de Residentes', key: 'jefe_de_residentes' },
  { name: 'Médico Especialista', key: 'medico_especialista' },
  { name: 'Alta Especialidad', key: 'alta_especialidad' },
  { name: 'Jefe de Servicio', key: 'jefe_de_servicio' },
  { name: 'Maestría en Ciencias', key: 'maestria_ciencias' },
  { name: 'Doctorado en Ciencias', key: 'doctorado_ciencias' },
  { name: 'Director de Hospital', key: 'director_hospital' },
  { name: 'Secretario de Salud', key: 'secretario_salud' },
  { name: 'Director de la OMS', key: 'director_oms' },
  { name: 'Premio Nobel', key: 'premio_nobel' },
];

const PhoneMockup = ({ src, alt }) => (
  <div className="relative mx-auto" style={{ width: '260px' }}>
    <div className="rounded-[2.5rem] border-[8px] border-gray-900 bg-gray-900 shadow-2xl overflow-hidden">
      <div className="bg-gray-900 h-6 flex items-center justify-center">
        <div className="w-20 h-4 bg-gray-800 rounded-full"></div>
      </div>
      <img
        src={src}
        alt={alt}
        className="w-full h-auto"
        loading="lazy"
      />
    </div>
  </div>
);

const PresentacionesVisual = () => (
  <div className="relative mx-auto" style={{ width: '260px' }}>
    <div className="rounded-[2.5rem] border-[8px] border-gray-900 bg-gray-900 shadow-2xl overflow-hidden">
      <div className="bg-gray-900 h-6 flex items-center justify-center">
        <div className="w-20 h-4 bg-gray-800 rounded-full"></div>
      </div>
      <div className="bg-white flex items-center justify-center" style={{ minHeight: '420px', padding: '24px 12px' }}>
        <img
          src="/screenshots/presentaciones.png"
          alt="Presentación de Anemias"
          className="w-full h-auto rounded-lg shadow-lg"
          loading="lazy"
        />
      </div>
    </div>
  </div>
);

const DuelosVisual = () => (
  <div className="relative mx-auto" style={{ width: '260px' }}>
    <div className="rounded-[2.5rem] border-[8px] border-gray-900 bg-gray-900 shadow-2xl overflow-hidden">
      <div className="bg-gray-900 h-6 flex items-center justify-center">
        <div className="w-20 h-4 bg-gray-800 rounded-full"></div>
      </div>
      <div className="relative">
        <img
          src="/screenshots/duelos.png"
          alt="Duelos entre usuarios"
          className="w-full h-auto"
          loading="lazy"
        />
        {/* Blur overlay on left user avatar */}
        <div className="absolute" style={{ left: '2%', top: '8.5%', width: '32%', height: '12%', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderRadius: '50%' }}></div>
        {/* Blur overlay on right user avatar */}
        <div className="absolute" style={{ right: '2%', top: '8.5%', width: '32%', height: '12%', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderRadius: '50%' }}></div>
        {/* Blur overlay on left user name + label */}
        <div className="absolute" style={{ left: '0%', top: '21%', width: '40%', height: '10%', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}></div>
        {/* Blur overlay on right user name + label */}
        <div className="absolute" style={{ right: '0%', top: '21%', width: '40%', height: '10%', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}></div>
      </div>
    </div>
  </div>
);

const FlashcardVisual = () => (
  <div className="relative mx-auto" style={{ width: '260px', perspective: '800px' }}>
    <div className="rounded-[2.5rem] border-[8px] border-gray-900 bg-gray-900 shadow-2xl overflow-hidden">
      <div className="bg-gray-900 h-6 flex items-center justify-center">
        <div className="w-20 h-4 bg-gray-800 rounded-full"></div>
      </div>
      <div className="bg-gray-100 p-4 flex flex-col items-center" style={{ minHeight: '420px' }}>
        <span className="text-[10px] text-gray-400 mb-1">Ginecología y Obstetricia</span>
        <span className="text-xs font-bold text-gray-700 mb-3">Flash Card 3/4</span>
        {/* Flipping card */}
        <div className="relative w-full" style={{ height: '280px', perspective: '600px' }}>
          <div className="w-full h-full" style={{ animation: 'flip-card 4s ease-in-out infinite', transformStyle: 'preserve-3d' }}>
            {/* Front */}
            <div className="absolute inset-0 bg-white rounded-2xl shadow-lg p-5 flex flex-col items-center justify-center" style={{ backfaceVisibility: 'hidden' }}>
              <span className="text-[10px] text-cyan-600 font-semibold mb-3">PREGUNTA</span>
              <p className="text-sm text-gray-800 text-center leading-relaxed font-medium">
                Respecto al número de consultas prenatales recomendado en embarazos de bajo riesgo, ¿qué estrategia es congruente con la guía?
              </p>
              <span className="mt-4 text-[10px] text-gray-400">Toca para voltear</span>
            </div>
            {/* Back */}
            <div className="absolute inset-0 bg-gray-800 rounded-2xl shadow-lg p-5 flex flex-col items-center justify-center" style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
              <span className="text-[10px] text-cyan-400 font-semibold mb-3">RESPUESTA</span>
              <p className="text-sm text-white text-center leading-relaxed">
                Realizar un mínimo de seis consultas distribuidas a lo largo de la gestación.
              </p>
              <div className="mt-4 flex gap-4">
                <span className="text-[10px] text-green-400">Correcta</span>
                <span className="text-[10px] text-gray-500">|</span>
                <span className="text-[10px] text-red-400">Repetir</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-1 mt-3">
          {[0,1,2,3].map(i => <div key={i} className={`w-2 h-2 rounded-full ${i === 2 ? 'bg-cyan-500' : 'bg-gray-300'}`}></div>)}
        </div>
      </div>
    </div>
    <style>{`
      @keyframes flip-card {
        0%, 40% { transform: rotateY(0deg); }
        50%, 90% { transform: rotateY(180deg); }
        100% { transform: rotateY(360deg); }
      }
    `}</style>
  </div>
);

const SimulacroVisual = () => (
  <div className="flex items-center gap-4">
    <PhoneMockup src="/screenshots/simulacro_question.png" alt="Simulacro pregunta" />
    <PhoneMockup src="/screenshots/simulacros.png" alt="Simulacro grid" />
  </div>
);

const mockRankingUsers = [
  { pos: 1, name: 'Dr. M. Hernández', pts: 187 },
  { pos: 2, name: 'A. García López', pts: 165 },
  { pos: 3, name: 'Dra. S. Ramírez', pts: 152 },
  { pos: 4, name: 'J. Rodríguez P.', pts: 140 },
  { pos: 5, name: 'L. Martínez V.', pts: 128 },
  { pos: 6, name: 'Dr. C. Flores R.', pts: 115 },
  { pos: 7, name: 'Dra. E. Sánchez', pts: 103 },
  { pos: 8, name: 'P. Torres García', pts: 97 },
  { pos: 9, name: 'R. Díaz Morales', pts: 85 },
  { pos: 10, name: 'Dra. V. López N.', pts: 72 },
];

const RankingVisual = () => (
  <div className="relative mx-auto max-w-[280px] space-y-4">
    {/* Top 10 List */}
    <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-4">
      <div className="text-center mb-3">
        <span className="text-sm font-bold text-gray-900">Ranking del Día</span>
        <p className="text-xs text-gray-400">Top 10 - Hoy</p>
      </div>
      <div className="space-y-1.5">
        {mockRankingUsers.map((u) => (
          <div key={u.pos} className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-50" style={{ filter: u.pos > 3 ? 'blur(1.5px)' : 'none' }}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
              u.pos === 1 ? 'bg-yellow-400 text-white' : u.pos === 2 ? 'bg-gray-300 text-white' : u.pos === 3 ? 'bg-orange-400 text-white' : 'bg-gray-100 text-gray-500'
            }`}>{u.pos}</span>
            <div className="w-6 h-6 rounded-full bg-gray-200 flex-shrink-0"></div>
            <span className="text-xs text-gray-700 flex-1 truncate">{u.name}</span>
            <span className="text-xs font-semibold text-gray-900">{u.pts} pts</span>
          </div>
        ))}
      </div>
    </div>
    {/* Journal preview */}
    <div className="rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
      <img src="/screenshots/journal.png" alt="Journal del Día" className="w-full h-auto" loading="lazy" />
    </div>
  </div>
);

const FeatureSection = ({ feature, index }) => {
  const isReversed = index % 2 !== 0;
  const Icon = feature.icon;

  return (
    <section
      className="py-16 sm:py-24 px-4"
      data-testid={`feature-${feature.id}`}
    >
      <div className={`max-w-6xl mx-auto flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 lg:gap-20`}>
        <div className="flex-1 text-center lg:text-left">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6"
            style={{ backgroundColor: `${feature.color}15` }}
          >
            <Icon size={28} style={{ color: feature.color }} />
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 leading-tight">
            {feature.title}
          </h3>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-lg mx-auto lg:mx-0">
            {feature.description}
          </p>
        </div>
        <div className="flex-shrink-0">
          {feature.customRender === 'presentaciones' ? (
            <PresentacionesVisual />
          ) : feature.customRender === 'duelos' ? (
            <DuelosVisual />
          ) : feature.customRender === 'ranking' ? (
            <RankingVisual />
          ) : feature.customRender === 'flashcards' ? (
            <FlashcardVisual />
          ) : feature.customRender === 'simulacros' ? (
            <SimulacroVisual />
          ) : (
            <PhoneMockup src={feature.screenshot} alt={feature.title} />
          )}
        </div>
      </div>
    </section>
  );
};

const HeroVideoCarousel = () => {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const videoRefs = useRef([]);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    setCurrent(api.selectedScrollSnap());
    api.on('select', onSelect);
    return () => { api.off('select', onSelect); };
  }, [api]);

  useEffect(() => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === current) {
        const setSpeed = () => { video.playbackRate = 2; };
        video.addEventListener('playing', setSpeed, { once: true });
        video.playbackRate = 2;
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [current]);

  return (
    <div className="relative w-full max-w-[90%] sm:max-w-[70%] lg:max-w-[50%] mx-auto px-14" data-testid="hero-video-carousel">
      <Carousel setApi={setApi} opts={{ loop: true, dragFree: false }}>
        <CarouselContent>
          {heroVideos.map((v, i) => (
            <CarouselItem key={i} className="flex justify-center">
              <div className="relative w-full">
                <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-black">
                  <video
                    ref={el => { videoRefs.current[i] = el; }}
                    src={v.url}
                    muted
                    loop
                    playsInline
                    autoPlay={i === 0}
                    preload="auto"
                    onPlay={e => { e.target.playbackRate = 2; }}
                    className="w-full block"
                  />
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Navigation arrows */}
      <button
        onClick={() => api?.scrollPrev()}
        className="absolute left-0 top-[45%] -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-md border border-gray-200 flex items-center justify-center hover:bg-white transition-colors z-10"
        data-testid="carousel-prev-btn"
      >
        <ChevronLeft size={20} className="text-gray-700" />
      </button>
      <button
        onClick={() => api?.scrollNext()}
        className="absolute right-0 top-[45%] -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-md border border-gray-200 flex items-center justify-center hover:bg-white transition-colors z-10"
        data-testid="carousel-next-btn"
      >
        <ChevronRight size={20} className="text-gray-700" />
      </button>

      {/* Feature title */}
      <p className="text-center text-xl sm:text-2xl font-bold text-gray-900 mt-5" data-testid="carousel-current-title">
        {heroVideos[current]?.title}
      </p>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2 mt-3" data-testid="carousel-dots">
        {heroVideos.map((_, i) => (
          <button
            key={i}
            onClick={() => api?.scrollTo(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === current ? 'bg-gray-900 w-6' : 'bg-gray-300 w-2'
            }`}
            data-testid={`carousel-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
};

const LandingPage = ({ onNavigateLogin }) => {
  const navigate = useNavigate();
  const [showAllRanks, setShowAllRanks] = useState(false);
  const [price, setPrice] = useState(1500);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/settings/price`)
      .then(res => setPrice(res.data.price))
      .catch(() => {});
  }, []);

  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToPrice = () => {
    const doScroll = () => {
      const ctaBtn = document.querySelector('[data-testid="pricing-cta-btn"]');
      if (ctaBtn) {
        ctaBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    doScroll();
    // Re-scroll after images may have loaded and shifted layout
    setTimeout(doScroll, 800);
    setTimeout(doScroll, 1600);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Sticky Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <span className="font-bold text-lg text-gray-900 tracking-tight">Proyecto Residente</span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/login')}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors px-3 py-2"
              data-testid="landing-login-btn"
            >
              Iniciar Sesión
            </button>
            <button
              onClick={scrollToPrice}
              className="text-sm font-semibold bg-gray-900 text-white px-5 py-2.5 rounded-full hover:bg-gray-800 transition-colors"
              data-testid="landing-register-btn"
            >
              Inscribirme
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col items-center justify-center px-4 pt-16">
        <div className="max-w-4xl mx-auto text-center">
          <img
            src={LOGO_URL}
            alt="Proyecto Residente"
            className="w-full max-w-md mx-auto mb-8"
          />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Tu preparación para el ENARM,<br />
            <span className="text-gray-500">como nunca antes</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-500 mb-10 max-w-2xl mx-auto leading-relaxed">
            La plataforma más completa para tu examen de residencia médica. Planificador inteligente, casos clínicos, simulacros, duelos y mucho más.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <button
              onClick={scrollToPrice}
              className="w-full sm:w-auto bg-gray-900 text-white font-semibold px-8 py-4 rounded-full text-lg hover:bg-gray-800 transition-all hover:shadow-lg flex items-center justify-center gap-2"
              data-testid="hero-cta-btn"
            >
              Comenzar ahora <ArrowRight size={20} />
            </button>
            <button
              onClick={scrollToFeatures}
              className="w-full sm:w-auto text-gray-600 font-medium px-8 py-4 rounded-full text-lg border border-gray-200 hover:border-gray-400 transition-all flex items-center justify-center gap-2"
            >
              Ver características <ChevronDown size={20} />
            </button>
          </div>

          {/* App preview - Video carousel */}
        </div>
        <div className="w-full mt-4">
          <HeroVideoCarousel />
        </div>

        <div className="mt-12 animate-bounce">
          <ChevronDown size={24} className="text-gray-300" />
        </div>
      </section>

      {/* Features */}
      <div id="features">
        {features.map((feature, index) => (
          <FeatureSection key={feature.id} feature={feature} index={index} />
        ))}
      </div>

      {/* Rank Progression Section */}
      <section className="py-16 sm:py-24 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-yellow-50 mb-6">
            <Star size={28} className="text-yellow-500" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Sistema de Progresión
          </h3>
          <p className="text-base sm:text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
            Acumula puntos con cada actividad. Progresa desde Estudiante de Medicina hasta el Premio Nobel en Medicina.
          </p>

          <div className="grid grid-cols-3 sm:grid-cols-5 gap-5 sm:gap-8">
            {(showAllRanks ? ranks : ranks.slice(0, 10)).map((rank) => (
              <div key={rank.key} className="flex flex-col items-center">
                <div className="flex items-end gap-1 mb-2">
                  <img
                    src={`/sprites/${rank.key}_male.png`}
                    alt={`${rank.name} masculino`}
                    className="object-contain"
                    style={{ width: '5rem', height: '5rem' }}
                    loading="lazy"
                  />
                  <img
                    src={`/sprites/${rank.key}_female.png`}
                    alt={`${rank.name} femenino`}
                    className="object-contain"
                    style={{ width: '5rem', height: '5rem' }}
                    loading="lazy"
                  />
                </div>
                <span className="text-xs sm:text-sm text-gray-700 font-medium text-center leading-tight">
                  {rank.name}
                </span>
              </div>
            ))}
          </div>

          {!showAllRanks && ranks.length > 10 && (
            <button
              onClick={() => setShowAllRanks(true)}
              className="mt-8 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors underline"
            >
              Ver todos los rangos ({ranks.length})
            </button>
          )}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 sm:py-24 px-4" data-testid="pricing-section">
        <div className="max-w-lg mx-auto text-center">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Acceso completo
          </h3>
          <p className="text-gray-500 mb-10">
            Todo lo que necesitas para tu preparación ENARM en un solo lugar.
          </p>

          <div className="bg-white border-2 border-gray-900 rounded-3xl p-8 sm:p-10 shadow-sm">
            <div className="mb-4 inline-block bg-green-100 text-green-800 text-sm font-semibold px-4 py-1.5 rounded-full">
              72 horas de prueba gratis
            </div>
            <div className="mb-8">
              <span className="text-5xl sm:text-6xl font-bold text-gray-900">${price.toLocaleString('es-MX')}</span>
              <span className="text-lg text-gray-500 ml-2">MXN</span>
              <p className="text-gray-500 mt-2">por 6 meses de acceso</p>
            </div>

            <ul className="text-left space-y-3 mb-10">
              {[
                'Planificador inteligente personalizado',
                'Cuestionarios con retroalimentación',
                'Casos clínicos Escape Room',
                'Imágenes diagnósticas de todas las áreas',
                'Presentaciones de repaso',
                'Flash cards personales',
                'Simulacros tipo ENARM con cronómetro',
                'Duelos y competencia entre usuarios',
                'Ranking diario + Journal',
                'Selector de especialidad',
                'Sistema de progresión gamificado',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={18} className="text-gray-900 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => navigate('/register')}
              className="w-full bg-gray-900 text-white font-semibold py-4 rounded-full text-lg hover:bg-gray-800 transition-all hover:shadow-lg flex items-center justify-center gap-2"
              data-testid="pricing-cta-btn"
            >
              Registrarme gratis <ArrowRight size={20} />
            </button>
          </div>

          <p className="text-xs text-gray-400 mt-6">
            Pago seguro procesado por MercadoPago. Tarjeta de crédito o débito.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-10 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-6">
            <Link to="/terminos" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">Términos y Condiciones</Link>
            <Link to="/privacidad" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">Aviso de Privacidad</Link>
            <Link to="/descargo" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">Descargo de Responsabilidad</Link>
            <Link to="/pagos-politica" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">Pagos y Suscripciones</Link>
            <Link to="/propiedad-intelectual" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">Propiedad Intelectual</Link>
          </div>
          <p className="text-center text-xs text-gray-300">
            Proyecto Residente - Decisión Médica
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
